import React from "react";
import {observer} from "mobx-react";
import styled from "@emotion/styled";
import {IMatch, IMatchSquad} from "data/types/entities";
import {getDashedValue} from "data/utils/helpers";
import {Typography, Stack} from "@mui/material";
import {SquadImage} from "views/components/squad_image/squad_image.component";
import {Exist} from "views/components/exist/exist.component";
import {MatchUtils} from "data/utils/match";
import {Empty} from "data/types/generics";
import SportsFootballIcon from "@mui/icons-material/SportsFootball";

const Squad = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	min-width: 80px;
	width: 100%;
	color: #000;
	gap: 6px;
	margin-bottom: 4px;
	font-weight: 600;
	font-size: 16px;
	line-height: 120%;
	letter-spacing: 0.02em;

	&.loss {
		opacity: 0.5;
	}
	img {
		height: 30px;
		min-width: 30px;
	}
`;

const SquadPoints = styled(Typography)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 32px;
	background: rgba(18, 18, 18, 0.05);
	margin-left: 20px;
	font-weight: 700;
	font-size: 18px;
	line-height: 120%;
	letter-spacing: 0.02em;

	.win & {
		border-right: 3px solid var(--primaryColor);
	}

	@media (max-width: 768px) {
		height: 25px;
		width: 35px;
	}
`;

const SquadInfo = styled(Stack)`
	align-items: flex-start;
	margin-right: auto;
	gap: 4px;
`;

const SquadName = styled(Typography)`
	margin-right: auto;
	display: flex;
	align-items: center;
	gap: 3px;
	text-align: left;
	svg {
		width: 15px;
		padding-bottom: 3px;
		color: var(--primaryColor);
		height: auto;
	}

	@media (max-width: 768px) {
		font-size: 14px;
		line-height: 1;
		svg {
			padding-bottom: 0;
		}
	}
`;

const Timeouts = styled.div<{count?: number}>`
	display: flex;
	gap: 4px;
	div {
		height: 2px;
		background: #151b34;
		width: 12px;

		&:nth-of-type(-n + ${(props) => props.count ?? 0}) {
			background: rgba(18, 18, 18, 0.15);
		}
	}
`;

interface IProps {
	match: IMatch;
	squad: IMatchSquad;
	className: Empty<string>;
	wdl: string;
	timeouts: number;
	isPossession: boolean;
}

export const MatchCardSquad: React.FC<IProps> = observer((props) => {
	const {squad, className, wdl, match, timeouts, isPossession} = props;
	return (
		<Squad className={className}>
			<SquadImage squadId={squad.id} />
			<SquadInfo>
				<SquadName variant="body1">
					<span>{squad.shortName}</span>
					<Exist when={isPossession && MatchUtils.isPlaying(match)}>
						<SportsFootballIcon />
					</Exist>
				</SquadName>
				<Exist when={MatchUtils.isPlaying(match)}>
					<Timeouts count={timeouts}>
						<div />
						<div />
					</Timeouts>
				</Exist>
				<Exist when={!MatchUtils.isPlaying(match)}>
					<Typography variant={"body2"}>{wdl}</Typography>
				</Exist>
			</SquadInfo>

			<SquadPoints variant="subtitle1">{getDashedValue(squad.score, "-", match)}</SquadPoints>
		</Squad>
	);
});
